'use client'

/**
 * AssistantWidget
 *
 * Floating launcher + panel anchored fixed bottom-right (desktop/tablet).
 * NOT a Drawer — intentionally non-modal so the user can read the
 * dashboard while consulting the assistant.
 *
 * z-40: above page content, but BELOW drawers/modals (z-50) so the
 * floating launcher/panel never covers a drawer's bottom-right Save button
 * (every Drawer/Modal renders a full-viewport inset-0 backdrop at z-50, so a
 * lower z-index here reliably tucks the widget beneath any of them). Still
 * below CommandPalette/Toast (z-[100]).
 * Animation: assistant-rise-in/out keyframes (globals.css) — the panel
 * rises from the launcher area with a fade, and sinks back on close.
 *
 * Conversation id + unread-dot state live in useAssistantWidgetState
 * (shared with the phone-width MobileAssistantChat). This component only
 * persists its own chrome state:
 *   assistant_open      — boolean
 *   assistant_expanded  — large-panel mode toggle
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import Logo from '@/components/ui/Logo'
import { AssistantPanel } from './AssistantPanel'
import {
  useAssistantWidgetState,
  readLS,
  writeLS,
  LS_OPEN_KEY,
  LS_EXPANDED_KEY,
} from './useAssistantWidgetState'
import { useTranslations } from 'next-intl'

export function AssistantWidget() {
  const t = useTranslations('assistant')
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  const {
    mounted,
    activeConversationId,
    onConversationChange,
    isStreaming,
    showUnreadDot,
  } = useAssistantWidgetState({ isOpen })

  // Hydrate chrome state from localStorage after mount to avoid SSR mismatch.
  useEffect(() => {
    if (readLS(LS_OPEN_KEY) === 'true') setIsOpen(true)
    if (readLS(LS_EXPANDED_KEY) === 'true') setIsExpanded(true)
  }, [])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
    writeLS(LS_OPEN_KEY, 'true')
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    writeLS(LS_OPEN_KEY, 'false')
  }, [])

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => {
      writeLS(LS_EXPANDED_KEY, prev ? 'false' : 'true')
      return !prev
    })
  }, [])

  // Exit animation: keep rendering the panel for the duration of the
  // slide-out (the slide-in keyframe played in reverse), then unmount.
  const [isClosing, setIsClosing] = useState(false)
  const prevOpenRef = useRef(isOpen)
  useEffect(() => {
    const wasOpen = prevOpenRef.current
    prevOpenRef.current = isOpen
    if (wasOpen && !isOpen) {
      setIsClosing(true)
      const timer = setTimeout(() => setIsClosing(false), 240)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Avoid rendering anything until client-side hydration is done.
  if (!mounted) return null

  return (
    <div className="z-40" style={{ position: 'fixed', bottom: '1.25rem', right: '1.25rem' }}>
      {isOpen || isClosing ? (
        // Panel: fixed container, non-modal (no backdrop overlay).
        // Enter/exit: rises from the launcher area while fading in,
        // sinks back down and fades out on close.
        <div
          className={`${
            isOpen
              ? 'animate-[assistant-rise-in_0.28s_cubic-bezier(0.4,0,0.2,1)_both]'
              : 'animate-[assistant-rise-out_0.22s_cubic-bezier(0.4,0,1,1)_both] pointer-events-none'
          } flex flex-col overflow-hidden`}
          style={{
            width: isExpanded ? 'min(760px, calc(100vw - 3rem))' : '400px',
            height: isExpanded ? 'calc(100vh - 3rem)' : 'min(640px, calc(100vh - 3rem))',
            maxHeight: 'calc(100vh - 3rem)',
            borderRadius: '1rem',
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface)',
            boxShadow: '0 24px 48px rgba(0,0,0,0.35), 0 8px 16px rgba(0,0,0,0.2)',
            transition:
              'width 300ms cubic-bezier(0.4,0,0.2,1), height 300ms cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <AssistantPanel
            activeConversationId={activeConversationId}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
            onConversationChange={onConversationChange}
            onClose={handleClose}
          />
        </div>
      ) : (
        // Launcher pill.
        <button
          type="button"
          onClick={handleOpen}
          title={t('widget.openAssistant')}
          aria-label={t('widget.openAssistant')}
          style={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'var(--color-surface)',
            borderRadius: '9999px',
            padding: '0.625rem 1rem',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-lg)',
            cursor: 'pointer',
            transition: 'border-color 200ms, box-shadow 200ms',
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget
            el.style.borderColor = 'var(--color-lime)'
            el.style.boxShadow = 'var(--shadow-lg), var(--shadow-glow-lime)'
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget
            el.style.borderColor = 'var(--color-border)'
            el.style.boxShadow = 'var(--shadow-lg)'
          }}
        >
          {/* Streaming ring — absolutely positioned div behind the pill */}
          {isStreaming && (
            <span
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: '-2.5px',
                borderRadius: '9999px',
                zIndex: -1,
                background:
                  'conic-gradient(from var(--ring-angle), transparent 0deg, transparent 250deg, var(--color-lime-muted) 285deg, var(--color-lime) 320deg, var(--color-cyan) 352deg, transparent 360deg)',
                animation: 'ring-travel 1.8s linear infinite',
              }}
            />
          )}

          <Logo width={54} height={20} />

          <span
            style={{
              fontSize: '0.875rem',
              fontWeight: 600,
              color: 'var(--color-text-primary)',
            }}
          >
            {t('widget.ask')}
          </span>

          {/* Unread dot */}
          {showUnreadDot && (
            <span
              aria-label={t('widget.unreadMessage')}
              style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '12px',
                height: '12px',
                borderRadius: '9999px',
                background: 'var(--color-pink)',
                border: '2px solid var(--color-surface)',
              }}
            />
          )}
        </button>
      )}
    </div>
  )
}
